/**
 * The app's one door to its data.
 *
 * Views, stores and hooks call `api.*` with the same method names and shapes
 * the HTTP API had, and failures still arrive as `ApiError` with a stable
 * `code`. Behind the door there is no server any more — plans and photos live in
 * this browser's IndexedDB (`lib/local/`) — but nothing outside this file and
 * that folder needs to know. If a server-backed feature ever returns, it plugs
 * in here.
 */

import { fileSafe, saveBlob } from './download'
import type { LocaleCode } from './i18n'
import {
  type Backup,
  type BackupMark,
  type RestoreOptions,
  type RestoreResult,
  backupMarks,
  createBackup,
  markBackedUp,
  readBackup,
  restoreBackup,
} from './local/backup'
import { getDb } from './local/db'
import { canDecodeHeic, processPhoto } from './local/images'
import { collectGarbage, readAssets, storePhoto } from './local/photos'
import * as plans from './local/plans'
import { type StorageReport, requestPersistentStorage, storageReport } from './local/storage'
import type { PhotoRef } from './pdf/model'
import { exportStatusFor } from './pdf/status'
import {
  ApiError,
  type EventKind,
  type ExportStatus,
  type FloorPlanDetection,
  type Plan,
  type PlanSummary,
  type SeatLabelMode,
  type UploadedAsset,
} from './types'

const today = (): string => new Date().toISOString().slice(0, 10)

/** Photos are printed small; 192 px keeps a guest-heavy PDF shareable by message. */
const PDF_PHOTO_EDGE = 192

/**
 * Every photo the plan uses, as JPEG bytes sized for print. Downscaled in the
 * browser where it can be; a photo that cannot be read is left out rather than
 * failing the export.
 */
async function photosForPdf(plan: Plan): Promise<Map<string, PhotoRef>> {
  const ids = plan.guests.flatMap((guest) => (guest.photoAssetId ? [guest.photoAssetId] : []))
  const photos = new Map<string, PhotoRef>()
  const assets = await readAssets(ids)
  const queue = [...assets]
  const worker = async () => {
    for (let asset = queue.shift(); asset; asset = queue.shift()) {
      try {
        const small = await processPhoto(asset.blob, PDF_PHOTO_EDGE)
        photos.set(asset.id, {
          key: asset.id,
          jpeg: new Uint8Array(await small.blob.arrayBuffer()),
          width: small.width,
          height: small.height,
        })
      } catch {
        if (asset.type !== 'image/jpeg') continue
        photos.set(asset.id, {
          key: asset.id,
          jpeg: new Uint8Array(await asset.blob.arrayBuffer()),
          width: asset.width,
          height: asset.height,
        })
      }
    }
  }
  // A few at a time: each decode holds a full bitmap in memory.
  await Promise.all(Array.from({ length: 4 }, worker))
  return photos
}

let floorPlanClient: Promise<typeof import('./floorplan/client')> | null = null

function loadFloorPlanClient(): Promise<typeof import('./floorplan/client')> {
  if (!floorPlanClient) {
    floorPlanClient = import('./floorplan/client').catch(() => {
      floorPlanClient = null
      throw new ApiError(503, 'import_unavailable', 'The photo reader could not be loaded.')
    })
  }
  return floorPlanClient
}

export const api = {
  /** Opens storage; rejects with `storage_unavailable` where the browser forbids it. */
  async ready(): Promise<void> {
    await getDb()
  },

  listPlans(): Promise<PlanSummary[]> {
    return plans.listPlans()
  },

  async createPlan(input: {
    /** Blank or missing: the kind's own title, in the plan's language. */
    title?: string
    eventKind?: EventKind
    eventDate?: string | null
    venue?: string | null
    language?: LocaleCode
    template?: 'blank' | 'starter'
  }): Promise<Plan> {
    const plan = await plans.createPlan(input)
    void requestPersistentStorage()
    return plan
  },

  getPlan(planId: string): Promise<Plan> {
    return plans.getPlan(planId)
  },

  updatePlan(
    planId: string,
    patch: {
      title?: string
      eventDate?: string | null
      venue?: string | null
      language?: LocaleCode
    },
  ): Promise<Plan> {
    return plans.updatePlan(planId, patch)
  },

  deletePlan(planId: string): Promise<void> {
    return plans.deletePlan(planId)
  },

  /** @param title the copy's name; defaults to "<title> (copy)" */
  duplicatePlan(planId: string, title?: string): Promise<Plan> {
    return plans.duplicatePlan(planId, title)
  },

  saveLayout(plan: Plan): Promise<Plan> {
    return plans.saveLayout(plan)
  },

  planVersion(planId: string): Promise<{ version: number; updatedAt: string }> {
    return plans.planVersion(planId)
  },

  /** Photos belong to the browser, not to one plan — duplicates and backups share them. */
  async uploadPhoto(_planId: string, file: File): Promise<UploadedAsset> {
    const { asset, url } = await storePhoto(await processPhoto(file))
    return { id: asset.id, url, width: asset.width, height: asset.height }
  },

  /** Whether this browser can open HEIC photos (Safari can; Chrome and Firefox cannot). */
  canReadHeic(): Promise<boolean> {
    return canDecodeHeic()
  },

  /** Deletes photos no plan uses any more (after a grace period). */
  collectGarbage(): Promise<number> {
    return collectGarbage()
  },

  /** What the plans take in this browser, and whether the browser has promised to keep them. */
  storageReport(): Promise<StorageReport> {
    return storageReport()
  },

  // --- backups ----------------------------------------------------------------

  /**
   * Download these plans, photos included, as one backup file, and remember
   * they are backed up at their current versions.
   */
  async downloadBackup(planIds: string[]): Promise<void> {
    const { blob, plans: included } = await createBackup(planIds)
    const name =
      included.length === 1
        ? `${fileSafe(included[0].title, 'plan')}-yerbeyer-${today()}.json`
        : `yerbeyer-backup-${today()}.json`
    saveBlob(blob, name)
    await markBackedUp(included)
  },

  readBackup(file: File): Promise<Backup> {
    return readBackup(file)
  },

  restoreBackup(backup: Backup, options: RestoreOptions): Promise<RestoreResult> {
    return restoreBackup(backup, options)
  },

  backupMarks(): Promise<Map<string, BackupMark>> {
    return backupMarks()
  },

  // --- the PDF ------------------------------------------------------------------

  /** How the plan would print — page counts per chair style. No fonts needed. */
  async exportStatus(planId: string, seatLabels?: SeatLabelMode): Promise<ExportStatus> {
    return exportStatusFor(await plans.getPlan(planId), seatLabels)
  },

  /**
   * Render the complete print-ready PDF in the browser and download it. The
   * renderer, pdf-lib and fontkit load on first use; fonts beyond Latin and
   * Cyrillic only when the plan's text needs them.
   */
  async downloadPdf(
    planId: string,
    opts: { floorPages?: number; seatLabels?: SeatLabelMode } = {},
  ): Promise<void> {
    const plan = await plans.getPlan(planId)
    if (plan.tables.length === 0) {
      throw new ApiError(422, 'empty_plan', 'Add at least one table before exporting.')
    }
    if (opts.floorPages !== undefined && ![1, 2, 4].includes(opts.floorPages)) {
      throw new ApiError(422, 'invalid_floor_pages', 'The floor plan spans 1, 2 or 4 pages.')
    }
    const [{ buildPlanPdf }, { fetchFont }, photos] = await Promise.all([
      import('./pdf/index'),
      import('./pdf/fonts/urls'),
      photosForPdf(plan),
    ])
    const bytes = await buildPlanPdf(plan, {
      loadFont: fetchFont,
      photos,
      floorPages: opts.floorPages ?? null,
      seatLabels: opts.seatLabels ?? null,
    })
    const stamp = today().replace(/-/g, '')
    saveBlob(
      new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' }),
      `${fileSafe(plan.title, 'seating-plan')}-${stamp}.pdf`,
    )
  },

  // --- floor-plan import -----------------------------------------------------------

  /** Start the photo reader (OpenCV, in a worker) loading ahead of the photo. */
  prepareFloorPlanImport(): void {
    loadFloorPlanClient()
      .then((client) => client.prepare())
      .catch(() => {
        // Reported, in words, if a photo is then read.
      })
  },

  /** Stop the photo reader and give its memory back. */
  releaseFloorPlanImport(): void {
    void floorPlanClient?.then((client) => client.release()).catch(() => {})
  },

  /**
   * Read a photo of a venue's printed seating chart into table proposals. The
   * photo never leaves the browser and nothing is written: the dialog applies
   * the proposals through the ordinary edit path, as one undoable step.
   */
  async importFloorPlan(_planId: string, file: File): Promise<FloorPlanDetection> {
    const client = await loadFloorPlanClient()
    return client.readFloorPlan(file)
  },
}

export { backupDue } from './local/backup'
export type { Backup, BackupMark, ConflictChoice } from './local/backup'
export { LOW_SPACE_BYTES, type StorageReport } from './local/storage'
